import { Injectable, Injector, Type, inject } from '@angular/core';
import { ActivatedRouteSnapshot, ActivationEnd } from '@angular/router';

import { LayoutNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { getToken } from '@frontend/sports/common/core/utils/router';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { AdaptiveLayoutService, AdaptiveLayoutState } from '@frontend/sports/layout/feature';
import { TopNavigationVersion } from '@frontend/sports/navigation/core/feature/models';
import { OnAppInit } from '@frontend/vanilla/core';
import { Subject, skip, takeUntil } from 'rxjs';

import { SubNavigationResolve } from './sub-navigation-resolve.service';
import { SubNavigationService } from './sub-navigation.service';

@Injectable({ providedIn: 'root' })
export class SubNavigationBootstrapService implements OnAppInit {
    private injector = inject(Injector);
    private routerEvents = inject(RouterEventsService);
    private adaptiveLayout = inject(AdaptiveLayoutService);
    private subNavigation = inject(SubNavigationService);
    private layoutNavConfig = inject(LayoutNavigationConfig);
    private productUtils = inject(SportsProductUtils);

    private validSubscription$ = new Subject<void>();
    private resolver?: SubNavigationResolve<any>;
    private lastResolverType?: any;
    private layoutState?: AdaptiveLayoutState;
    private lastActivationEnd?: ActivationEnd;
    private readonly logger: SportsRemoteLogger;

    constructor() {
        const loggerFactory = inject(LoggerFactory);

        this.logger = loggerFactory.getLogger('SubNavigationBootstrapService');
    }

    onAppInit(): void {
        this.adaptiveLayout.stateChange$.pipe(this.productUtils.filterSportsEmitLast()).subscribe((state) => {
            this.layoutState = state;
            this.activationEnded(this.lastActivationEnd);
        });

        this.routerEvents.currentActivationEnd.pipe(this.productUtils.filterSports()).subscribe((event) => this.activationEnded(event));

        this.productUtils.fromSportsEnter().subscribe(() => this.activationEnded(this.lastActivationEnd));
        this.productUtils.fromSportsLeave().subscribe(() => this.cleanUpNavigation());
    }

    private activationEnded(event: ActivationEnd | undefined): void {
        if (!event) {
            return;
        }

        this.lastActivationEnd = event;

        const resolverType = this.findFirstSubnavigationResolver(event.snapshot);
        if (!resolverType || !this.shouldShowSubNav(event.snapshot)) {
            this.cleanUpNavigation();

            return;
        }

        if (resolverType !== this.lastResolverType) {
            this.renewSubscription();
            this.resolver?.cleanup();

            this.resolver = getToken<SubNavigationResolve<any>>(resolverType, event.snapshot, this.injector);

            if (!this.resolver) {
                this.logger.error(`Could not resolve Subnavigation resolver: ${resolverType} - route: ${event}`);

                return;
            }

            this.resolver.subNavigation$.pipe(skip(1), takeUntil(this.validSubscription$)).subscribe((items) => this.subNavigation.setTabs(items));
            this.resolver.tabSize$.pipe(takeUntil(this.validSubscription$)).subscribe((items) => this.subNavigation.setTabSize(items));
            this.resolver.tabFullWidth$.pipe(takeUntil(this.validSubscription$)).subscribe((items) => this.subNavigation.setFullWith(items));
            this.lastResolverType = resolverType;
        }

        const model = event.snapshot.data.model;

        if (this.resolver) {
            this.resolver.buildSubNavigation(this.layoutState || this.adaptiveLayout.current, event.snapshot, model);
        }
    }

    private cleanUpNavigation() {
        this.renewSubscription();
        this.lastResolverType = null;
        this.subNavigation.setTabs([]);
        this.subNavigation.setTabSize('large');

        if (this.resolver) {
            this.resolver.cleanup();
        }
    }

    private renewSubscription(): void {
        this.validSubscription$.next();
        this.validSubscription$.complete();
        this.validSubscription$ = new Subject<void>();
    }

    private findFirstSubnavigationResolver(snapshot: ActivatedRouteSnapshot): Type<SubNavigationResolve<any>> {
        let crtSnapshot: ActivatedRouteSnapshot | null = snapshot;

        while (crtSnapshot && (!crtSnapshot.data || !crtSnapshot.data.subNavigation)) {
            crtSnapshot = crtSnapshot.parent;
        }

        return crtSnapshot && crtSnapshot.data.subNavigation;
    }

    private shouldShowSubNav(snapshot: ActivatedRouteSnapshot): boolean {
        const crtSnapshot: ActivatedRouteSnapshot | null = snapshot;

        return this.layoutNavConfig.topNavigationVersion !== TopNavigationVersion.V3 || crtSnapshot.data?.shouldKeepSubNav;
    }
}
